import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity, AlertTriangle, BarChart3, Eye, KeyRound, Loader2, Palette,
  RefreshCw, RotateCw, ShieldAlert, SlidersHorizontal, Ticket, Webhook,
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { formatMoney } from './lib/formatters';
import {
  CREDIT_STATUS_LABELS, CREDIT_STATUS_TONE, canRetryCredit, errorText,
  ignoredFields, partnerApi,
} from './lib/emsportsPartner';
import {
  Busy, Empty, Field, IgnoredFields, JsonEditor, Panel, Problem, Toggle,
} from './components/EmSportsBits';
import EmSportsAppearance from './components/EmSportsAppearance';
import EmSportsTrading from './components/EmSportsTrading';
import ConfirmDialog from './components/ConfirmDialog';
import './emsports.css';

/**
 * EveryMatrix Sportsbook — self-servis yönetim ekranı (Partner API).
 *
 * Sağlayıcının **iki yüzeyi** vardır: para akışı (cüzdan callback'i, bahis,
 * cashout) ve yönetim. Birincisi kendiliğinden çalışır ve panelde yalnızca
 * ayar kartı olarak görünür (Oyun Kontrol → Spor & Cashout); bu ekran
 * ikincisidir — sağlayıcının kendi hub panelinde yapılan her şey buradan da
 * yapılabilir.
 *
 * Ekranın var olma sebebi tek bir cümledir: **ödemesi ölü kalan kupon.**
 * Sonuç ödemesi bizim cüzdanımıza ulaşamazsa sağlayıcı on sekiz kez dener,
 * sonra `dead` işaretler ve kendiliğinden bir daha denemez — o kuponu
 * kuyruğa geri koymanın tek yolu buradaki düğmedir. Kalan sekmeler (risk,
 * görünürlük, cashout, trading, marka, webhook) aynı yüzeyin öteki
 * parçalarıdır ve hepsi sağlayıcının kaydına yazar: bizim veritabanımızda
 * karşılıkları **yoktur**, bu yüzden her sekme açıldığında okunur.
 */

const TABS = [
  { id: 'summary', label: 'Özet', icon: BarChart3 },
  { id: 'bets', label: 'Kuponlar', icon: Ticket },
  { id: 'risk', label: 'Risk & Limitler', icon: ShieldAlert },
  { id: 'visibility', label: 'Görünürlük & Cashout', icon: Eye },
  { id: 'appearance', label: 'Görünüm', icon: Palette },
  { id: 'trading', label: 'Trading & Markalar', icon: Activity },
  { id: 'webhooks', label: 'Webhook & Anahtar', icon: Webhook },
];

const CREDIT_FILTERS = [
  { value: '', label: 'Tümü' },
  { value: 'dead', label: 'Ölü ödeme' },
  { value: 'failed', label: 'Başarısız' },
  { value: 'pending', label: 'Kuyrukta' },
  { value: 'done', label: 'Ödendi' },
  { value: 'none', label: 'Ödeme yok' },
];

const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const Stat = ({ label, value, hint, tone = '' }) => (
  <div className="ems-stat">
    <div className="ems-stat-label">{label}</div>
    <div className={`ems-stat-value ${tone}`}>{value}</div>
    {hint && <div className="ems-stat-hint">{hint}</div>}
  </div>
);

// --------------------------------------------------------------------------- #
// Özet
// --------------------------------------------------------------------------- #
const SummaryTab = () => {
  const [state, setState] = useState({ loading: true });

  const load = useCallback(async () => {
    setState({ loading: true });
    try {
      const [summary, alerts, flags] = await Promise.all([
        partnerApi.summary(),
        partnerApi.alerts(50).catch(() => ({ data: {} })),
        partnerApi.flags().catch(() => ({ data: {} })),
      ]);
      setState({
        loading: false,
        summary: summary.data || {},
        alerts: alerts.data?.alerts || alerts.data?.items || [],
        flags: flags.data?.flags || flags.data?.items || [],
      });
    } catch (error) {
      setState({ loading: false, error: errorText(error) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (state.loading) return <Busy />;
  if (state.error) return <Problem>{state.error}</Problem>;

  const summary = state.summary || {};
  return (
    <div className="space-y-4">
      <Panel
        title="Sağlayıcıdaki özet"
        description={
          'Rakamlar sağlayıcının kendi defterinden gelir ve bizim '
          + 'raporlarımızdan ayrıdır: reddedilen bahisler sayılmaz, iptal '
          + 'edilenler hacme girmez. GGR = bahis − ödeme; komisyon bunun '
          + 'üzerinden Sportsbook cüzdanınızdan kesilir.'
        }
        actions={(
          <button
            type="button"
            className="admin-btn-secondary inline-flex items-center gap-2 !py-1 !text-[11px]"
            onClick={load}
          >
            <RefreshCw size={12} aria-hidden="true" /> Yenile
          </button>
        )}
      >
        <div className="ems-stats">
          <Stat
            label="Kupon"
            value={number(summary.bets).toLocaleString('tr-TR')}
            hint={`Açık: ${number(summary.open_bets)}`}
          />
          <Stat
            label="Hacim"
            value={formatMoney(number(summary.turnover))}
            hint={`Son 24 sa: ${formatMoney(number(summary.turnover_24h))}`}
          />
          <Stat label="Ödeme" value={formatMoney(number(summary.payout))} />
          <Stat
            label="GGR"
            value={formatMoney(number(summary.ggr))}
            tone={number(summary.ggr) < 0 ? 'is-bad' : 'is-good'}
            hint="Negatif GGR: kupon kazandı, cüzdan artar"
          />
        </div>
      </Panel>

      <Panel
        title="Uyarılar"
        description={
          'Sağlayıcının kendi uyarı defteri. Ödeme ölü kaldığında '
          + '(dead_credit) ve yanıtı alınamayan bir bahis geri alındığında '
          + '(debit_unknown) burada satır oluşur — ikisi de Kuponlar '
          + 'sekmesinden çözülür.'
        }
      >
        {state.alerts?.length ? (
          <ul className="space-y-1">
            {state.alerts.slice(0, 30).map((row, index) => (
              <li
                key={row.alert_id || row.id || index}
                className="ems-row"
              >
                <AlertTriangle
                  size={13}
                  aria-hidden="true"
                  className={row.severity === 'critical'
                    ? 'ems-credit-dead' : 'ems-credit-failed'}
                />
                <span className="ems-row-title">
                  {row.text || row.content || row.category || 'Uyarı'}
                </span>
                {row.bet_id && (
                  <span className="ems-row-sub">kupon #{row.bet_id}</span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <Empty>Uyarı yok.</Empty>
        )}
      </Panel>

      <Panel
        title="İşaretlenen oyuncu ve maçlar"
        description={
          'İnceleme işareti bahsi **engellemez**: sağlayıcı yalnızca dikkat '
          + 'çeker. Eşikler Risk & Limitler sekmesindeki userFlags / '
          + 'matchFlags alanlarındadır.'
        }
      >
        {state.flags?.length ? (
          <ul className="space-y-1">
            {state.flags.slice(0, 30).map((row, index) => (
              <li
                key={row.id || index}
                className="ems-row"
              >
                {row.text || row.reason || JSON.stringify(row).slice(0, 160)}
              </li>
            ))}
          </ul>
        ) : (
          <Empty>İşaretlenen kayıt yok.</Empty>
        )}
      </Panel>

    </div>
  );
};

// --------------------------------------------------------------------------- #
// Kuponlar
// --------------------------------------------------------------------------- #
const BetsTab = () => {
  const { toast } = useToast();
  const [creditStatus, setCreditStatus] = useState('dead');
  const [player, setPlayer] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setProblem('');
    try {
      const params = { limit: 100 };
      if (creditStatus) params.credit_status = creditStatus;
      if (player.trim()) params.player = player.trim();
      const { data } = await partnerApi.bets(params);
      setRows(data?.bets || []);
      setTotal(number(data?.total));
    } catch (error) {
      setProblem(errorText(error, 'Kupon listesi alınamadı'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [creditStatus, player]);

  useEffect(() => { load(); }, [load]);

  const retry = async (row) => {
    const id = String(row.bet_id ?? row.id ?? '');
    setBusy(id);
    try {
      const { data } = await partnerApi.retryCredit(id);
      toast({
        title: 'Ödeme yeniden kuyruğa alındı',
        description:
          `Kupon #${id} · ${data?.previous_status || '—'} → `
          + `${data?.credit_status || 'pending'}. Ödeme yaklaşık 20 saniye `
          + 'içinde ilk denemedeki kimlikle çıkar; bizde zaten işlenmişse '
          + 'tekrar sayılmaz.',
      });
      await load();
    } catch (error) {
      toast({
        title: 'Ödeme kuyruğa alınamadı',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="space-y-4">
      <Panel
        title="Sağlayıcıdaki kuponlar"
        description={
          'Bu liste sağlayıcının defteridir ve bizim Bahis Geçmişi '
          + 'ekranımızdan ayrıdır: oradaki satırlar para hareketinden doğar, '
          + 'buradaki satır ödemenin sağlayıcı tarafındaki durumunu taşır. '
          + 'İkisi yalnızca bir durumda ayrışır ve o durum buradan çözülür: '
          + 'ödemesi başarısız ya da ölü kalan kupon.'
        }
        actions={(
          <button
            type="button"
            className="admin-btn-secondary inline-flex items-center gap-2 !py-1 !text-[11px]"
            onClick={load}
          >
            <RefreshCw size={12} aria-hidden="true" /> Yenile
          </button>
        )}
      >
        {/* Sabit piksel genişlik "Ölü ödeme" yazan kutuyu kırpıyordu:
            genişliği ızgara hücresi verir (bkz. emsports.css). */}
        <div className="ems-filters" style={{ marginBottom: 14 }}>
          <Field label="Ödeme durumu">
            <select
              className="admin-input"
              value={creditStatus}
              onChange={(event) => setCreditStatus(event.target.value)}
            >
              {CREDIT_FILTERS.map((row) => (
                <option key={row.value} value={row.value}>{row.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Oyuncu kimliği">
            <input
              className="admin-input"
              value={player}
              placeholder="örn. player_123…"
              spellCheck={false}
              autoComplete="off"
              onChange={(event) => setPlayer(event.target.value)}
            />
          </Field>
        </div>

        <Problem>{problem}</Problem>

        {loading ? <Busy /> : null}

        {!loading && !rows.length && (
          <Empty>
            {creditStatus === 'dead'
              ? 'Ölü ödeme yok — sağlayıcının bütün ödemeleri bize ulaşmış.'
              : 'Bu süzgeçle kupon bulunamadı.'}
          </Empty>
        )}

        {!loading && rows.length > 0 && (
          <>
            <p className="ems-field-hint" aria-live="polite" style={{ marginBottom: 8 }}>
              {total.toLocaleString('tr-TR')} kayıttan {rows.length} tanesi.
            </p>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Kupon</th>
                    <th>Oyuncu</th>
                    <th>Bahis</th>
                    <th>Ödeme</th>
                    <th>Durum</th>
                    <th>Ödeme durumu</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const id = String(row.bet_id ?? row.id ?? '');
                    const credit = String(row.credit_status || 'none');
                    return (
                      <tr key={id}>
                        <td><b>#{id}</b></td>
                        <td>{row.external_player_id || row.player || '—'}</td>
                        <td>{formatMoney(number(row.stake))}</td>
                        <td>{formatMoney(number(row.payout))}</td>
                        <td>{row.status || '—'}</td>
                        <td className={CREDIT_STATUS_TONE[credit] || ''}>
                          {CREDIT_STATUS_LABELS[credit] || credit}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {canRetryCredit(credit) && (
                            <button
                              type="button"
                              className="admin-btn-primary inline-flex items-center gap-1"
                              disabled={busy === id}
                              onClick={() => retry(row)}
                            >
                              {busy === id
                                ? <Loader2 size={11} className="animate-spin" aria-hidden="true" />
                                : <RotateCw size={11} aria-hidden="true" />}
                              Yeniden kuyruğa al
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Panel>
    </div>
  );
};

// --------------------------------------------------------------------------- #
// Risk & limitler
// --------------------------------------------------------------------------- #
const RiskTab = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState('');
  const [risk, setRisk] = useState({});
  const [ignored, setIgnored] = useState([]);
  const [rules, setRules] = useState([]);
  const [draft, setDraft] = useState({
    scope: 'player', scope_id: '', max_stake: '', max_odds: '', blocked: false,
  });
  // Yıkıcı işlem doğrudan uygulanmaz: kural silindiğinde o kapsamdaki limit
  // anında kalkar ve geri alma yolu yoktur.
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setProblem('');
    try {
      const [config, ruleRes] = await Promise.all([
        partnerApi.config(),
        partnerApi.riskRules().catch(() => ({ data: {} })),
      ]);
      setRisk(config.data?.risk || {});
      const list = ruleRes.data?.rules || ruleRes.data?.items || [];
      setRules(Array.isArray(list) ? list : []);
    } catch (error) {
      setProblem(errorText(error, 'Risk yapılandırması alınamadı'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy('config');
    try {
      const { data } = await partnerApi.saveRiskConfig({ risk });
      const missed = ignoredFields(data);
      setIgnored(missed);
      toast({
        title: missed.length
          ? 'Kaydedildi — bazı alanlar uygulanmadı'
          : 'Risk yapılandırması kaydedildi',
        description: missed.length ? missed.join(', ') : undefined,
        variant: missed.length ? 'destructive' : undefined,
      });
    } catch (error) {
      toast({
        title: 'Kaydedilemedi',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  const addRule = async () => {
    const body = { scope: draft.scope };
    if (draft.scope !== 'merchant') body.scope_id = draft.scope_id.trim();
    if (draft.max_stake) body.max_stake = Number(draft.max_stake);
    if (draft.max_odds) body.max_odds = Number(draft.max_odds);
    if (draft.blocked) body.blocked = true;
    setBusy('rule');
    try {
      await partnerApi.saveRiskRule(body);
      setDraft({
        scope: 'player', scope_id: '', max_stake: '', max_odds: '',
        blocked: false,
      });
      toast({ title: 'Risk kuralı kaydedildi' });
      await load();
    } catch (error) {
      toast({
        title: 'Kural kaydedilemedi',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  if (loading) return <Busy />;

  return (
    <div className="space-y-4">
      <Problem>{problem}</Problem>

      <Panel
        title="Katmanlı risk yapılandırması"
        description={
          'Bloklar: general, single, multiple, inplaySingle, inplayMultiple, '
          + 'withBonus, perCurrency, perCountry, userFlags, matchFlags. Alan '
          + 'adları camelCase’dir (maxStake, minStake, maxPayout, maxLegs, '
          + 'minOdd). Tanınmayan alan uygulanmaz ve kayıttan sonra '
          + '"uygulanmayan alanlar" olarak yazılır — yazım hatası sessizce '
          + 'sizi korumasız bırakmasın diye.'
        }
        actions={(
          <button
            type="button"
            className="admin-btn-secondary inline-flex items-center gap-2 !py-1 !text-[11px]"
            onClick={load}
          >
            <RefreshCw size={12} aria-hidden="true" /> Yenile
          </button>
        )}
      >
        <JsonEditor value={risk} onChange={setRisk} rows={16} />
        <IgnoredFields fields={ignored} />
        <button
          type="button"
          className="admin-btn-primary mt-2 inline-flex items-center gap-2 !py-1 !text-[11px]"
          disabled={busy === 'config'}
          onClick={save}
        >
          {busy === 'config'
            ? <Loader2 size={12} className="animate-spin" aria-hidden="true" />
            : null}
          Risk yapılandırmasını kaydet
        </button>
      </Panel>

      <Panel
        title="Kapsamlı kurallar"
        description={
          'Tek bir oyuncuya, spora, turnuvaya ya da maça kural yazar: bahsi '
          + 'reddeder, tutarı kısar ya da oran tavanı koyar. Merchant '
          + 'yapılandırmasının üstünde çalışır.'
        }
      >
        {rules.length ? (
          <ul className="mb-3 space-y-1">
            {rules.map((row) => (
              <li
                key={row.id}
                className="ems-row"
              >
                <span className="ems-row-title">
                  {row.scope}{row.scope_id ? ` · ${row.scope_id}` : ''}
                </span>
                {row.blocked && (
                  <span className="ems-credit-dead">engelli</span>
                )}
                {row.max_stake ? (
                  <span>maks bahis {formatMoney(number(row.max_stake))}</span>
                ) : null}
                {row.max_odds ? (
                  <span>maks oran {row.max_odds}</span>
                ) : null}
                <button
                  type="button"
                  className="admin-btn-danger ems-row-end"
                  onClick={() => setPendingDelete(row)}
                >
                  Sil
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <Empty>Kapsamlı kural yok.</Empty>
        )}

        <div className="ems-form">
          <Field label="Kapsam">
            <select
              className="admin-input"
              value={draft.scope}
              onChange={(event) => setDraft(
                (old) => ({ ...old, scope: event.target.value }))}
            >
              {['merchant', 'player', 'player_country', 'sport', 'country',
                'tournament', 'event', 'market'].map((scope) => (
                  <option key={scope} value={scope}>{scope}</option>
              ))}
            </select>
          </Field>
          <Field label="Kimlik">
            <input
              className="admin-input"
              disabled={draft.scope === 'merchant'}
              value={draft.scope_id}
              onChange={(event) => setDraft(
                (old) => ({ ...old, scope_id: event.target.value }))}
            />
          </Field>
          <Field label="Maks. bahis">
            <input
              type="number"
              className="admin-input"
              value={draft.max_stake}
              onChange={(event) => setDraft(
                (old) => ({ ...old, max_stake: event.target.value }))}
            />
          </Field>
          <Field label="Maks. oran">
            <input
              type="number"
              step="0.01"
              className="admin-input"
              value={draft.max_odds}
              onChange={(event) => setDraft(
                (old) => ({ ...old, max_odds: event.target.value }))}
            />
          </Field>
          <div className="flex items-end">
            <Toggle
              label="Engelle"
              checked={draft.blocked}
              onChange={(checked) => setDraft(
                (old) => ({ ...old, blocked: checked }))}
            />
          </div>
          <div className="md:col-span-5">
            <button
              type="button"
              className="admin-btn-primary inline-flex items-center gap-2 !py-1 !text-[11px]"
              disabled={busy === 'rule'
                || (draft.scope !== 'merchant' && !draft.scope_id.trim())}
              onClick={addRule}
            >
              Kuralı kaydet
            </button>
          </div>
        </div>
      </Panel>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Risk kuralını sil"
        description={pendingDelete
          ? `${pendingDelete.scope}${pendingDelete.scope_id ? ` · ${pendingDelete.scope_id}` : ''} kapsamındaki kural kaldırılacak; o kapsamda yalnızca merchant yapılandırması kalır.`
          : ''}
        confirmLabel="Sil"
        tone="danger"
        busy={busy === 'rule-delete'}
        onCancel={() => setPendingDelete(null)}
        onConfirm={async () => {
          setBusy('rule-delete');
          try {
            await partnerApi.deleteRiskRule(pendingDelete.id);
            toast({ title: 'Kural silindi' });
            setPendingDelete(null);
            await load();
          } catch (error) {
            toast({
              title: 'Kural silinemedi',
              description: errorText(error),
              variant: 'destructive',
            });
          } finally {
            setBusy('');
          }
        }}
      />
    </div>
  );
};

// --------------------------------------------------------------------------- #
// Görünürlük & cashout & ayarlar
// --------------------------------------------------------------------------- #
const VisibilityTab = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState('');
  const [visibility, setVisibility] = useState({});
  const [settings, setSettings] = useState({});
  const [cashout, setCashout] = useState({
    enabled: true, partial_enabled: true, auto_enabled: true,
    margin_pct: 5, tolerance_pct: 2, min_amount: 0.1,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setProblem('');
    try {
      const { data } = await partnerApi.config();
      setVisibility(data?.visibility || {});
      setSettings(data?.settings || {});
      if (data?.cashout && typeof data.cashout === 'object') {
        setCashout((old) => ({ ...old, ...data.cashout }));
      }
    } catch (error) {
      setProblem(errorText(error, 'Yapılandırma alınamadı'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (key, action, title) => {
    setBusy(key);
    try {
      await action();
      toast({ title });
      await load();
    } catch (error) {
      toast({
        title: 'Kaydedilemedi',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  if (loading) return <Busy />;

  return (
    <div className="space-y-4">
      <Problem>{problem}</Problem>

      <Panel
        title="Erken kapatma (cashout)"
        description={
          'Oyuncunun kuponunu bozdurabilmesi, kısmi ve otomatik bozdurma, '
          + 'teklifin marjı ve toleransı burada. Marj teklifin üzerinden '
          + 'kesilir (0–50); tolerans, teklif kabul edilirken fiyatın ne '
          + 'kadar düşebileceğidir. Bozdurma ödemesi bize credit olarak '
          + 'gelir ve kupon satırında görünür.'
        }
      >
        <div className="grid gap-3 md:grid-cols-3">
          <Toggle
            label="Cashout açık"
            checked={cashout.enabled !== false}
            onChange={(checked) => setCashout(
              (old) => ({ ...old, enabled: checked }))}
          />
          <Toggle
            label="Kısmi bozdurma"
            hint="Kupon kalan bahisle yürümeye devam eder."
            checked={cashout.partial_enabled !== false}
            onChange={(checked) => setCashout(
              (old) => ({ ...old, partial_enabled: checked }))}
          />
          <Toggle
            label="Otomatik bozdurma"
            hint="Oyuncu eşik koyar; teklif eşiğe gelince kupon bozulur."
            checked={cashout.auto_enabled !== false}
            onChange={(checked) => setCashout(
              (old) => ({ ...old, auto_enabled: checked }))}
          />
          <Field label="Marj (%)">
            <input
              type="number"
              min={0}
              max={50}
              className="admin-input admin-input-w-xs"
              value={cashout.margin_pct ?? 5}
              onChange={(event) => setCashout((old) => ({
                ...old, margin_pct: Number(event.target.value),
              }))}
            />
          </Field>
          <Field label="Tolerans (%)">
            <input
              type="number"
              min={0}
              className="admin-input admin-input-w-xs"
              value={cashout.tolerance_pct ?? 2}
              onChange={(event) => setCashout((old) => ({
                ...old, tolerance_pct: Number(event.target.value),
              }))}
            />
          </Field>
          <Field label="Alt sınır">
            <input
              type="number"
              step="0.01"
              min={0}
              className="admin-input admin-input-w-xs"
              value={cashout.min_amount ?? 0.1}
              onChange={(event) => setCashout((old) => ({
                ...old, min_amount: Number(event.target.value),
              }))}
            />
          </Field>
        </div>
        <button
          type="button"
          className="admin-btn-primary mt-3 inline-flex items-center gap-2 !py-1 !text-[11px]"
          disabled={busy === 'cashout'}
          onClick={() => save(
            'cashout',
            () => partnerApi.saveCashout({ cashout }),
            'Cashout ayarları kaydedildi',
          )}
        >
          {busy === 'cashout'
            ? <Loader2 size={12} className="animate-spin" aria-hidden="true" />
            : null}
          Cashout ayarlarını kaydet
        </button>
      </Panel>

      <Panel
        title="Görünürlük"
        description={
          'Gizlenen spor, ülke, turnuva, maç ve market kümeleri. Gizli bir '
          + 'kaleme gelen bahis her zaman reddedilir (market_unavailable); '
          + 'önceden açılmış kuponlar etkilenmez. Gövdenin tamamı yazılır: '
          + 'önce okuyun, değiştirin, eksiksiz gönderin.'
        }
      >
        <JsonEditor
          value={visibility}
          onChange={setVisibility}
          rows={10}
          hint='Örnek: {"markets": {"off": ["18"]}, "sports": {"off": []}}'
        />
        <button
          type="button"
          className="admin-btn-primary mt-2 inline-flex items-center gap-2 !py-1 !text-[11px]"
          disabled={busy === 'visibility'}
          onClick={() => save(
            'visibility',
            () => partnerApi.saveVisibility(visibility),
            'Görünürlük kaydedildi',
          )}
        >
          Görünürlüğü kaydet
        </button>
      </Panel>

      <Panel
        title="Kitap ayarları"
        description={
          'Bahis türleri, diller, para birimleri ve bildirim anahtarları. '
          + 'Maç durumu bildirimi (notifications.match_status) açıldığında '
          + 'skor değişiklikleri cüzdan adresimize gelir; karşılanır ve para '
          + 'hareketi doğurmaz. Yalnızca gönderdiğiniz üst düzey anahtarlar '
          + 'değişir.'
        }
      >
        <JsonEditor
          value={settings}
          onChange={setSettings}
          rows={10}
          hint='Örnek: {"notifications": {"match_status": true}}'
        />
        <button
          type="button"
          className="admin-btn-primary mt-2 inline-flex items-center gap-2 !py-1 !text-[11px]"
          disabled={busy === 'settings'}
          onClick={() => save(
            'settings',
            () => partnerApi.saveSettings({ settings }),
            'Kitap ayarları kaydedildi',
          )}
        >
          Ayarları kaydet
        </button>
      </Panel>
    </div>
  );
};

// --------------------------------------------------------------------------- #
// Webhook, uyarı kanalı, anahtar
// --------------------------------------------------------------------------- #
const WebhookTab = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState('');
  const [webhooks, setWebhooks] = useState({});
  const [channel, setChannel] = useState({});
  const [queue, setQueue] = useState([]);
  const [confirmRotate, setConfirmRotate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setProblem('');
    try {
      const [hooks, alerts, queued] = await Promise.all([
        partnerApi.webhooks(),
        partnerApi.alertChannel().catch(() => ({ data: {} })),
        partnerApi.webhookQueue().catch(() => ({ data: {} })),
      ]);
      setWebhooks(hooks.data || {});
      setChannel(alerts.data?.alerts || alerts.data || {});
      const rows = queued.data?.queue || queued.data?.items || [];
      setQueue(Array.isArray(rows) ? rows : []);
    } catch (error) {
      setProblem(errorText(error, 'Webhook ayarları alınamadı'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rotate = async () => {
    setBusy('rotate');
    try {
      const { data } = await partnerApi.rotateKey();
      toast({
        title: 'API secret yenilendi',
        description:
          'Yeni sır kaydedildi. Eski anahtar bir süre daha kabul edilir, '
          + 'yani açık kuponların bildirimleri düşmez'
          + (data?.grace_active ? ' (geçiş penceresi açık).' : '.'),
      });
      setConfirmRotate(false);
    } catch (error) {
      toast({
        title: 'Anahtar yenilenemedi',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  if (loading) return <Busy />;

  return (
    <div className="space-y-4">
      <Problem>{problem}</Problem>

      <Panel
        title="Olay bildirimleri"
        description={
          'Bahis konulduğunda, sonuçlandığında ve bozdurulduğunda ayrı bir '
          + 'adrese olay gönderilir. Bu, cüzdan callback’inin yerine geçmez; '
          + 'para yine cüzdan ucundan akar. Bahis öncesi onay '
          + '(requires_prebet_validation) açılmadan önce onu karşılayan uç '
          + 'yazılmış olmalıdır — hata dönen bir onay ucu **her bahsi** '
          + 'reddeder. Bizim cüzdan ucumuz validate_bet’i zaten karşılar.'
        }
        actions={(
          <button
            type="button"
            className="admin-btn-secondary inline-flex items-center gap-2 !py-1 !text-[11px]"
            onClick={load}
          >
            <RefreshCw size={12} aria-hidden="true" /> Yenile
          </button>
        )}
      >
        <JsonEditor
          value={webhooks}
          onChange={setWebhooks}
          rows={8}
          hint='Örnek: {"webhook_url": "https://siteniz.com/hook", "events": ["bet_settled"]}'
        />
        <button
          type="button"
          className="admin-btn-primary mt-2 inline-flex items-center gap-2 !py-1 !text-[11px]"
          disabled={busy === 'hooks'}
          onClick={async () => {
            setBusy('hooks');
            try {
              await partnerApi.saveWebhooks(webhooks);
              toast({ title: 'Webhook ayarları kaydedildi' });
              await load();
            } catch (error) {
              toast({
                title: 'Kaydedilemedi',
                description: errorText(error),
                variant: 'destructive',
              });
            } finally {
              setBusy('');
            }
          }}
        >
          Webhook ayarlarını kaydet
        </button>

        {queue.length > 0 && (
          <div className="mt-3">
            <div className="ems-stat-label" style={{ marginBottom: 6 }}>
              Kuyruk
            </div>
            <ul className="space-y-1">
              {queue.slice(0, 20).map((row, index) => (
                <li
                  key={row.id || index}
                  className="ems-row"
                >
                  {row.event || 'olay'} · {row.status || '—'}
                  {row.bet_id ? ` · kupon #${row.bet_id}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Panel>

      <Panel
        title="Uyarı kanalı"
        description={
          'Sağlayıcı bir uyarı ürettiğinde (ölü ödeme, yanıtsız bahis) sizi '
          + 'nereden haberdar edeceği. Telegram jetonu okunurken geri '
          + 'dönmez; yalnızca tanımlı olup olmadığı bildirilir. Kanal '
          + 'tanımlanmadan önce üretilen uyarılar sonradan gönderilmez — '
          + 'geçmiş yalnızca Özet sekmesinden okunur.'
        }
      >
        <JsonEditor
          value={channel}
          onChange={setChannel}
          rows={7}
          hint='Örnek: {"telegram_chat": "@kanal", "min_severity": "warn"}'
        />
        <button
          type="button"
          className="admin-btn-primary mt-2 inline-flex items-center gap-2 !py-1 !text-[11px]"
          disabled={busy === 'channel'}
          onClick={async () => {
            setBusy('channel');
            try {
              await partnerApi.saveAlertChannel({ alerts: channel });
              toast({ title: 'Uyarı kanalı kaydedildi' });
              await load();
            } catch (error) {
              toast({
                title: 'Kaydedilemedi',
                description: errorText(error),
                variant: 'destructive',
              });
            } finally {
              setBusy('');
            }
          }}
        >
          Uyarı kanalını kaydet
        </button>
      </Panel>

      <Panel
        title="API secret yenileme"
        description={
          'Sağlayıcı yeni bir sır üretir ve **o andan itibaren onunla** '
          + 'imzalar. Yeni sır burada kendiliğinden kaydedilir; eski sır bir '
          + 'süre daha kabul edilmeye devam eder, yani açık kuponların '
          + 'bildirimleri düşmez. Sır panele hiçbir zaman geri dönmez.'
        }
      >
        {confirmRotate ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="ems-note ems-note-warn">
              Anahtar hemen değişecek. Sağlayıcı panelinde elle bir yere
              yazdığınız sır varsa geçersiz kalır.
            </span>
            <button
              type="button"
              className="admin-btn-danger inline-flex items-center gap-2 !py-1 !text-[11px]"
              disabled={busy === 'rotate'}
              onClick={rotate}
            >
              {busy === 'rotate'
                ? <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                : <KeyRound size={12} aria-hidden="true" />}
              Evet, yenile
            </button>
            <button
              type="button"
              className="admin-btn-secondary !py-1 !text-[11px]"
              onClick={() => setConfirmRotate(false)}
            >
              Vazgeç
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="admin-btn-secondary inline-flex items-center gap-2 !py-1 !text-[11px]"
            onClick={() => setConfirmRotate(true)}
          >
            <KeyRound size={12} aria-hidden="true" /> API secret’i yenile
          </button>
        )}
      </Panel>
    </div>
  );
};

// --------------------------------------------------------------------------- #
// Kabuk
// --------------------------------------------------------------------------- #
const EmSportsManager = () => {
  const [tab, setTab] = useState('summary');

  const body = useMemo(() => {
    switch (tab) {
      case 'bets': return <BetsTab />;
      case 'risk': return <RiskTab />;
      case 'visibility': return <VisibilityTab />;
      case 'appearance': return <EmSportsAppearance />;
      case 'trading': return <EmSportsTrading />;
      case 'webhooks': return <WebhookTab />;
      default: return <SummaryTab />;
    }
  }, [tab]);

  return (
    // Sayfa kendi iç boşluğunu yazmaz: ``.admin-main`` zaten veriyor ve
    // ikisi üst üste binince içerik başlıktan kopuyordu.
    <div className="admin-page ems-page">
      <header className="admin-page-heading">
        <div className="admin-page-heading-icon">
          <SlidersHorizontal size={21} aria-hidden="true" />
        </div>
        <div>
          <h1>EveryMatrix Yönetimi</h1>
          <p>
            Sağlayıcının self-servis yönetim yüzeyi (Partner API). Buradaki
            ayarların hiçbiri bizim veritabanımızda durmaz — hepsi doğrudan
            sağlayıcıya yazılır ve sayfa her açıldığında oradan okunur.
            Entegrasyonun kendi ayarları (merchant key, secret, callback
            onayı) <b>Oyun Kontrol → Spor &amp; Cashout</b> altındadır.
          </p>
        </div>
      </header>

      {/* Sekme kalıbı panelin kendi bileşenidir (`.admin-tabs`): hap biçimi,
          44px dokunma hedefi ve **tema vurgusu**. Ham bir Tailwind rengi
          yeşil vurgulu bir kurulumda yabancı dururdu. */}
      <nav className="admin-tabs" aria-label="EveryMatrix yönetim bölümleri">
        {TABS.map((item) => {
          const Icon = item.icon;
          const active = tab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={`inline-flex items-center gap-2 ${active ? 'active' : ''}`}
              aria-current={active ? 'page' : undefined}
            >
              <Icon size={14} aria-hidden="true" /> {item.label}
            </button>
          );
        })}
      </nav>

      {body}
    </div>
  );
};

export default EmSportsManager;
